import React, { useState, useEffect } from 'react';
import { X, HelpCircle, Check, Loader, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { getSupabaseClient } from '../../../supabaseClient';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

// Component metadata for the modal
export const configMetadata = {
  title: 'Curbside Configuration',
  helpIcon: true
};

const CurbsideLaundries = ({ connectionId, onConfigSaved, onCancel, onShowHelp }) => {
  // State for form inputs
  const [url, setUrl] = useState('');
  const [signupUrl, setSignupUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // State for loading, errors, and settings
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetupInstructions, setShowSetupInstructions] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState('not_started');
  const [formIsValid, setFormIsValid] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // Expose help function to parent
  useEffect(() => {
    if (onShowHelp) {
      // Pass the function to show instructions to the parent
      onShowHelp(() => setShowSetupInstructions(true));
    }
  }, [onShowHelp]);
  
  // Fetch user data on load
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;
        if (user) {
          setUserData(user);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to authenticate user');
      }
    };
    
    fetchUserData();
  }, []);

  // Handle input change
  const handleInputChange = (field, value) => {
    // Update the specific field state
    switch (field) {
      case 'url':
        setUrl(value);
        break;
      case 'signupUrl':
        setSignupUrl(value);
        break;
      case 'username':
        setUsername(value);
        break;
      case 'password':
        setPassword(value);
        break;
      default:
        break;
    }
    
    // Check form validity on every change
    setTimeout(() => {
      const isValid = 
        (field === 'url' ? value.trim() : url.trim()) !== '' && 
        (field === 'username' ? value.trim() : username.trim()) !== '' && 
        (field === 'password' ? value.trim() : password.trim()) !== '';
      
      setFormIsValid(isValid);
    }, 0);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    // Basic validation check – keep it simple.
    if (!formIsValid) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      // Ensure we have the current user and a stable accountId for this transaction.
      let accountId = userData?.id;
      if (!accountId) {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;
        if (!user) throw new Error('User not authenticated');
        accountId = user.id;
        setUserData(user); // update state for future interactions
      }

      // 1️⃣  Ensure a row exists in curbside_settings
      let currentSettingsId;
      const { data: existingSettings, error: fetchErr } = await supabase
        .from('curbside_settings')
        .select('id')
        .eq('account_id', accountId)
        .maybeSingle();

      if (fetchErr && fetchErr.code !== 'PGRST116') throw fetchErr;

      if (existingSettings) {
        currentSettingsId = existingSettings.id;
        // Update existing row with latest form values
        await supabase
          .from('curbside_settings')
          .update({
            url: url.trim(),
            signup_url: signupUrl.trim() || '',
            username: username.trim(),
            password: password.trim(),
            updated_at: new Date().toISOString()
          })
          .eq('id', currentSettingsId);
      } else {
        const { data: newSettings, error: insertErr } = await supabase
          .from('curbside_settings')
          .insert({
            account_id: accountId,
            url: url.trim(),
            signup_url: signupUrl.trim() || '',
            username: username.trim(),
            password: password.trim(),
            service_options: {},
            integration_status: 'not_started',
            updated_at: new Date().toISOString()
          })
          .select('id')
          .single();

        if (insertErr) throw insertErr;
        currentSettingsId = newSettings.id;
      }

      // 2️⃣  Create (or ensure) joint-row in connections_config
      if (connectionId) {
        const { error: configError } = await supabase
          .from('connections_config')
          .upsert({
            connection_id: connectionId,
            account_id: accountId
          }, {
            onConflict: 'account_id,connection_id'
          });

        if (configError && configError.code !== '23505') {
          // 23505 = duplicate key; safe to ignore
          throw configError;
        }
      }

      // 3️⃣  Mark integration as in_progress
      await supabase
        .from('curbside_settings')
        .update({ integration_status: 'in_progress' })
        .eq('id', currentSettingsId);

      setIntegrationStatus('in_progress');

      // 4️⃣  Kick off backend setup
      try {
        const res = await fetch('https://cleaner-automation-936dbbf97f9e.herokuapp.com/api/browser/curbside/setup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountId })
        });

        if (!res.ok) console.error('API setup request failed');
        else toast.success('Curbside integration setup started');
      } catch (apiErr) {
        console.error('API request error:', apiErr);
      }

      toast.success('Curbside configuration saved successfully');

      // Notify parent – keep UX snappy.
      onConfigSaved();
    } catch (err) {
      console.error('Error saving Curbside config:', err);
      setError(err.message || 'Failed to save configuration');
      toast.error(err.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="w-full">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-md text-red-700 text-sm flex items-center">
          <span className="mr-2">
            <X className="w-4 h-4" />
          </span>
          {error}
        </div>
      )}
      
      {/* Main form */}
      <div className="space-y-4">
        {/* Subdomain field */}
        <div>
          <Label htmlFor="url" className="text-sm font-medium">
            Curbside Subdomain <span className="text-red-500">*</span>
          </Label>
          <div className="mt-1 relative">
            <Input
              id="url"
              placeholder="subdomain"
              value={url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              className="pr-[180px]"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none pr-3">
              <span className="text-gray-500 text-sm">-pds.curbsidelaundries.com</span>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Enter only the subdomain part of your Curbside URL
          </p>
        </div>
        
        {/* Signup URL field */}
        <div>
          <Label htmlFor="signup_url" className="text-sm font-medium">
            Customer Signup URL
          </Label>
          <Input
            id="signup_url"
            placeholder="https://example.com/signup"
            value={signupUrl}
            onChange={(e) => handleInputChange('signupUrl', e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            The URL to send customers when they want to create a new account
          </p>
        </div>
        
        {/* Username field */}
        <div>
          <Label htmlFor="username" className="text-sm font-medium">
            Username <span className="text-red-500">*</span>
          </Label>
          <Input
            id="username"
            placeholder="bella@aiforlaundromats.com"
            value={username}
            onChange={(e) => handleInputChange('username', e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Email used for the employee account you created
          </p>
        </div>
        
        {/* Password field */}
        <div>
          <Label htmlFor="password" className="text-sm font-medium">
            Password <span className="text-red-500">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => handleInputChange('password', e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Password for the employee account you created
          </p>
        </div>
        
        {/* Submit button */}
        <div className="pt-4 flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={loading || !formIsValid}
            className="w-full"
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
      
      {/* Setup Instructions Modal */}
      {showSetupInstructions && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10001] overflow-y-auto backdrop-blur-sm">
          <div className="bg-white rounded-md w-full max-w-[700px] mx-auto my-8 shadow-sm border border-gray-200">
            <div className="p-6 pb-4 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Curbside Setup Instructions</h2>
                <p className="text-gray-500 text-sm mt-1">Follow these steps to connect your account.</p>
              </div>
              <button
                onClick={() => setShowSetupInstructions(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="px-6 pb-6 overflow-y-auto">
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">1</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Access Admin Interface</h3>
                    <div className="bg-gray-50 rounded mt-2 p-2">
                      <code className="text-sm">https://<span className="text-blue-600">your-subdomain</span>-pds.curbsidelaundries.com/User</code>
                    </div>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">2</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Create Employee Account</h3>
                    <p className="text-sm text-gray-600 mt-1 mb-2">Click + New Employee and enter:</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex">
                        <div className="w-24 text-gray-500">Name</div>
                        <div>Any name</div>
                      </div>
                      <div className="flex">
                        <div className="w-24 text-gray-500">Email</div>
                        <div>bella@aiforlaundromats.com</div>
                      </div>
                      <div className="flex">
                        <div className="w-24 text-gray-500">Password</div>
                        <div>Create a secure password</div>
                      </div>
                      <div className="flex">
                        <div className="w-24 text-gray-500">Phone</div>
                        <div>Your business number</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">3</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Set Permissions</h3>
                    <div className="space-y-2 text-sm mt-2">
                      <div className="flex">
                        <div className="text-gray-500 w-32">Current Role:</div>
                        <div>Manager</div>
                      </div>
                      <div className="flex">
                        <div className="text-gray-500 w-32">Switchable Roles:</div>
                        <div>All options</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mr-4 mt-0.5">
                    <div className="w-6 h-6 bg-black rounded-full text-white flex items-center justify-center text-sm font-medium">4</div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Complete Setup</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Save the employee account and enter your credentials on this page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100">
              <Button
                onClick={() => setShowSetupInstructions(false)}
                className="bg-gray-900 hover:bg-gray-800 text-white w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Attach metadata to component for parent modal to access
CurbsideLaundries.configMetadata = configMetadata;

export default CurbsideLaundries; 